import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Card, Button, Tag, Input, Form, Select, Empty, Alert, Tabs, List, Badge, message, Space, Timeline, Collapse } from 'antd'
import { 
  CheckCircleOutlined, 
  CloseCircleOutlined,
  EyeOutlined,
  MessageOutlined,
  HistoryOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  SaveOutlined,
  FileTextOutlined,
  CheckSquareOutlined
} from '@ant-design/icons'
import type { BiddingCase, AnnotationData } from '../../types/index'
import { mockCases, mockAnnotationData } from '../../services/mockData'

const { TextArea } = Input
const { Option } = Select
const { TabPane } = Tabs
const { Panel } = Collapse

// 左栏：待审核案件列表
const ReviewCaseList = ({ cases, selectedCaseId, onSelectCase }: { 
  cases: BiddingCase[], 
  selectedCaseId?: string,
  onSelectCase: (caseData: BiddingCase) => void 
}) => {
  return (
    <div className="h-full flex flex-col bg-white rounded-lg border">
      <div className="p-3 border-b bg-gray-50">
        <div className="font-medium">📋 待审核案件</div>
        <div className="text-xs text-gray-400 mt-1">共 {cases.length} 个</div>
      </div>
      <div className="flex-1 overflow-auto">
        {cases.length === 0 ? (
          <Empty description="暂无待审核案件" className="mt-8" />
        ) : (
          <div className="divide-y">
            {cases.map((caseData) => (
              <div
                key={caseData.caseId}
                className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                  selectedCaseId === caseData.caseId ? 'bg-blue-50 border-l-4 border-primary' : ''
                }`}
                onClick={() => onSelectCase(caseData)}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-sm">{caseData.caseId}</span>
                  <Badge status="processing" text="待审核" />
                </div>
                <p className="text-xs text-gray-600 line-clamp-2">{caseData.projectName}</p>
                <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                  <Tag>标注员: 张三</Tag>
                  <span>2小时前</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// 中栏：标注内容展示
const ReviewContentPanel = ({ annotationData }: { annotationData: AnnotationData | null }) => {
  if (!annotationData) {
    return (
      <div className="h-full flex items-center justify-center bg-white rounded-lg border">
        <Empty description="请选择案件" />
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-white rounded-lg border">
      <div className="p-3 border-b bg-gray-50 flex items-center justify-between">
        <div className="font-medium">📄 标注内容</div>
        <Tag color="blue">{annotationData.docMeta.case_id}</Tag>
      </div>
      
      <div className="flex-1 overflow-auto p-4">
        <Tabs defaultActiveKey="question">
          <TabPane tab="质疑函" key="question">
            {/* 质疑函元数据 */}
            <Card className="mb-4 bg-blue-50">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-gray-500">文档类型:</span> {annotationData.docMeta.doc_type}</div>
                <div><span className="text-gray-500">发文日期:</span> {annotationData.docMeta.issue_date}</div>
                <div className="col-span-2"><span className="text-gray-500">项目名称:</span> {annotationData.docMeta.project_name}</div>
                <div className="col-span-2"><span className="text-gray-500">质疑方:</span> {annotationData.docMeta.questioner?.name}</div>
                <div className="col-span-2"><span className="text-gray-500">意图摘要:</span> {annotationData.docMeta.intent_summary}</div>
              </div>
            </Card>

            {/* 质疑事项列表 */}
            <Collapse defaultActiveKey={['0']}>
              {annotationData.questionDetails.map((q, idx) => (
                <Panel 
                  key={idx} 
                  header={
                    <div className="flex items-center gap-2">
                      <Badge count={idx + 1} style={{ backgroundColor: '#1890ff' }} />
                      <Tag color="blue">{q.topic}</Tag>
                      <span className="text-sm truncate max-w-md">{q.question_summary}</span>
                    </div>
                  }
                >
                  <div className="space-y-3 text-sm">
                    <div>
                      <div className="font-medium text-gray-700">质疑详细内容:</div>
                      <div className="mt-1 p-2 bg-gray-50 rounded">{q.question_detail}</div>
                    </div>
                    <div>
                      <div className="font-medium text-gray-700">事实依据:</div>
                      <ul className="list-disc list-inside mt-1">
                        {q.evidence_facts.map((fact, i) => (
                          <li key={i} className="text-gray-600">{fact}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <div className="font-medium text-gray-700">援引法规:</div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {q.laws_regulations_cited.map((law, i) => (
                          <Tag key={i}>《{law.name}》{law.articles.map(a => a.article_index).join(',')}</Tag>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="font-medium text-gray-700">请求措施:</div>
                      <div className="text-primary">{q.requested_action}</div>
                    </div>
                  </div>
                </Panel>
              ))}
            </Collapse>
          </TabPane>

          <TabPane tab="答复函" key="reply">
            {/* 答复函元数据 */}
            <Card className="mb-4 bg-green-50">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-gray-500">文档类型:</span> {annotationData.docMeta.doc_type}</div>
                <div><span className="text-gray-500">答复方:</span> {annotationData.docMeta.responder?.name}</div>
                <div className="col-span-2"><span className="text-gray-500">整体结论:</span> <Tag color="blue">{annotationData.docMeta.overall_decision}</Tag></div>
              </div>
            </Card>

            {/* 答复事项列表 */}
            <Collapse defaultActiveKey={['0']}>
              {annotationData.replyDetails.map((r, idx) => (
                <Panel 
                  key={idx} 
                  header={
                    <div className="flex items-center gap-2">
                      <Badge count={idx + 1} style={{ backgroundColor: '#52c41a' }} />
                      <Tag color={r.reply_position === '不采纳' ? 'red' : r.reply_position === '完全采纳' ? 'green' : 'orange'}>
                        {r.reply_position}
                      </Tag>
                      <span className="text-sm">关联事项 #{annotationData.questionDetails.findIndex(q => q.item_id === r.related_inquiry_item_id) + 1}</span>
                    </div>
                  }
                >
                  <div className="space-y-3 text-sm">
                    <div>
                      <div className="font-medium text-gray-700">质疑点复述:</div>
                      <div className="mt-1">{r.question_summary}</div>
                    </div>
                    <div>
                      <div className="font-medium text-gray-700">回复理由/论证:</div>
                      <div className="mt-1 p-2 bg-gray-50 rounded">{r.reply_reasoning}</div>
                    </div>
                    <div>
                      <div className="font-medium text-gray-700">最终结论:</div>
                      <div className="mt-1 text-primary font-medium">{r.reply_conclusion}</div>
                    </div>
                    <div>
                      <div className="font-medium text-gray-700">援引法规:</div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {r.laws_regulations_cited.map((law, i) => (
                          <Tag key={i} color="green">《{law.name}》</Tag>
                        ))}
                      </div>
                    </div>
                  </div>
                </Panel>
              ))}
            </Collapse>
          </TabPane>

          <TabPane tab="NER实体" key="ner">
            <Card className="mb-4">
              <div className="font-medium mb-2">已标注实体 ({annotationData.entities.length}个)</div>
              <div className="flex flex-wrap gap-2">
                {annotationData.entities.map((entity) => (
                  <Tag key={entity.id} color="blue" className="text-sm">
                    {entity.text} <span className="text-gray-400">[{entity.label}]</span>
                  </Tag>
                ))}
              </div>
            </Card>
          </TabPane>

          <TabPane tab="审核历史" key="history">
            <Timeline>
              <Timeline.Item dot={<CheckCircleOutlined style={{ color: '#52c41a' }} />}>
                <div className="text-sm">
                  <div className="font-medium">标注完成</div>
                  <div className="text-gray-500">2026-02-22 14:30:25 by 标注员张三</div>
                </div>
              </Timeline.Item>
              <Timeline.Item dot={<MessageOutlined style={{ color: '#faad14' }} />}>
                <div className="text-sm">
                  <div className="font-medium">提交审核</div>
                  <div className="text-gray-500">2026-02-22 14:35:10 by 标注员张三</div>
                  <div className="mt-1 text-gray-600">备注：请重点审核答复理由的完整性</div>
                </div>
              </Timeline.Item>
            </Timeline>
          </TabPane>
        </Tabs>
      </div>
    </div>
  )
}

// 右栏：审核操作面板
const ReviewActionPanel = ({ onApprove, onReject, onSave }: { 
  onApprove: () => void, 
  onReject: () => void,
  onSave: () => void
}) => {
  const [form] = Form.useForm()

  return (
    <div className="h-full flex flex-col bg-white rounded-lg border">
      <div className="p-3 border-b bg-gray-50">
        <div className="font-medium">✅ 审核操作</div>
      </div>
      
      <div className="flex-1 overflow-auto p-4">
        <Form form={form} layout="vertical">
          <Form.Item
            name="reviewResult"
            label="审核结果"
            rules={[{ required: true, message: '请选择审核结果' }]}
          >
            <Select placeholder="请选择">
              <Option value="approved">
                <span className="text-green-600">✓ 审核通过</span>
              </Option>
              <Option value="rejected">
                <span className="text-red-600">✗ 退回修改</span>
              </Option>
              <Option value="partial">
                <span className="text-orange-600">◐ 部分通过</span>
              </Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="qualityScore"
            label="质量评分"
          >
            <Select placeholder="请选择质量等级">
              <Option value="excellent">⭐⭐⭐⭐⭐ 优秀</Option>
              <Option value="good">⭐⭐⭐⭐ 良好</Option>
              <Option value="qualified">⭐⭐⭐ 合格</Option>
              <Option value="pending">⭐⭐ 待改进</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="reviewComments"
            label="审核意见"
            rules={[{ required: true, message: '请填写审核意见' }]}
          >
            <TextArea 
              rows={8} 
              placeholder="请详细描述审核意见，包括：&#10;1. 标注质量评价&#10;2. 存在的问题&#10;3. 修改建议"
            />
          </Form.Item>

          <Form.Item>
            <Space direction="vertical" className="w-full">
              <Button 
                type="primary" 
                icon={<CheckCircleOutlined />} 
                block 
               
                onClick={onApprove}
                className="bg-green-600"
              >
                审核通过
              </Button>
              <Button 
                danger 
                icon={<CloseCircleOutlined />} 
                block 
               
                onClick={onReject}
              >
                退回修改
              </Button>
              <Button 
                icon={<SaveOutlined />} 
                block
                onClick={onSave}
              >
                保存草稿
              </Button>
            </Space>
          </Form.Item>
        </Form>

        <Card title="📋 快速检查项" className="mt-4">
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircleOutlined className="text-green-500" />
              <span>质疑-答复配对完整</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircleOutlined className="text-green-500" />
              <span>元数据填写完整</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircleOutlined className="text-green-500" />
              <span>法规引用规范</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircleOutlined className="text-green-500" />
              <span>结论标签正确</span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}

// 主组件
const ReviewWorkspace = () => {
  const { caseId } = useParams()
  const navigate = useNavigate()
  const [selectedCase, setSelectedCase] = useState<BiddingCase | null>(null)
  const [annotationData, setAnnotationData] = useState<AnnotationData | null>(null)

  // 待审核案件列表
  const pendingCases = mockCases.filter(c => c.status === 'annotating' || c.status === 'reviewing')

  // 加载案件数据
  useEffect(() => {
    if (caseId) {
      const caseData = mockCases.find(c => c.caseId === caseId)
      if (caseData) {
        setSelectedCase(caseData)
        const annotation = mockAnnotationData[caseId]
        if (annotation) {
          setAnnotationData(annotation)
        }
      }
    }
  }, [caseId])

  const handleSelectCase = (caseData: BiddingCase) => {
    setSelectedCase(caseData)
    const annotation = mockAnnotationData[caseData.caseId]
    if (annotation) {
      setAnnotationData(annotation)
    }
    navigate(`/review/${caseData.caseId}`)
  }

  const handleApprove = () => {
    message.success('审核通过')
  }

  const handleReject = () => {
    message.success('已退回修改')
  }

  const handleSave = () => {
    message.success('保存成功')
  }

  return (
    <div className="h-[calc(100vh-112px)] flex flex-col">
      {/* 顶部工具栏 */}
      <div className="bg-white p-3 rounded-lg shadow-sm mb-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold">🔍 审核工作台</h2>
          {selectedCase && (
            <>
              <span className="text-gray-300">|</span>
              <span className="font-mono text-primary">{selectedCase.caseId}</span>
              <span className="text-gray-500 text-sm">{selectedCase.projectName}</span>
              <Badge count="待审核" style={{ backgroundColor: '#faad14' }} />
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button icon={<ArrowLeftOutlined />}>上一个</Button>
          <Button icon={<ArrowRightOutlined />}>下一个</Button>
        </div>
      </div>

      {/* 三栏式布局 */}
      <div className="flex-1 grid grid-cols-[280px_1fr_380px] gap-3 min-h-0">
        {/* 左栏：案件列表 */}
        <ReviewCaseList 
          cases={pendingCases}
          selectedCaseId={selectedCase?.caseId}
          onSelectCase={handleSelectCase}
        />

        {/* 中栏：标注内容 */}
        <ReviewContentPanel annotationData={annotationData} />

        {/* 右栏：审核操作 */}
        <ReviewActionPanel 
          onApprove={handleApprove}
          onReject={handleReject}
          onSave={handleSave}
        />
      </div>
    </div>
  )
}

export default ReviewWorkspace
